import React, { useEffect, useRef } from 'react';
import { C } from '../shared/constants';

export default function ParticlesCanvas() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const particlesRef = useRef([]);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const colors = [C.turquesa, C.mango, C.coral];
    const colorRGB = { [C.turquesa]:[93,193,185], [C.mango]:[253,190,2], [C.coral]:[235,99,107] };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobileDevice = window.innerWidth < 768;
    const particleCount = isMobileDevice ? 15 : 60;

    function resize() {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    }
    resize();
    window.addEventListener('resize', resize);

    if (particlesRef.current.length === 0) {
      for (let i = 0; i < particleCount; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        particlesRef.current.push({
          x, y, origX: x, origY: y,
          r: 2 + Math.random() * 2,
          color: colors[Math.floor(Math.random() * colors.length)],
          opacity: 0.3 + Math.random() * 0.2,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
        });
      }
    }

    function handleMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    if (!isMobileDevice) window.addEventListener('mousemove', handleMouseMove);

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const particles = particlesRef.current;
      const mouse = mouseRef.current;

      particles.forEach(p => {
        if (!reducedMotion) {
          p.origX += p.vx; p.origY += p.vy;
          if (p.origX < 0 || p.origX > canvas.width) p.vx *= -1;
          if (p.origY < 0 || p.origY > canvas.height) p.vy *= -1;
          p.origX = Math.max(0, Math.min(canvas.width, p.origX));
          p.origY = Math.max(0, Math.min(canvas.height, p.origY));
          p.x += (p.origX - p.x) * 0.05;
          p.y += (p.origY - p.y) * 0.05;
          if (!isMobileDevice) {
            const dx = p.x - mouse.x, dy = p.y - mouse.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 100 && dist > 0) {
              const force = (100 - dist) / 100 * 2.5;
              p.x += (dx / dist) * force;
              p.y += (dy / dist) * force;
            }
          }
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i], b = particles[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            const rgbA = colorRGB[a.color] || [255,255,255];
            const rgbB = colorRGB[b.color] || [255,255,255];
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(${Math.round((rgbA[0]+rgbB[0])/2)},${Math.round((rgbA[1]+rgbB[1])/2)},${Math.round((rgbA[2]+rgbB[2])/2)},${(1 - dist / 120) * 0.25})`;
            ctx.lineWidth = 0.8;
            ctx.globalAlpha = 1;
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
      if (!isMobileDevice) window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <canvas ref={canvasRef} style={{
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
      pointerEvents: 'none', zIndex: 0,
    }} />
  );
}
